import { useState, useCallback, useEffect, useRef, ChangeEvent, Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { CSVFileEntry, DatenformOption, DatenanpassungOption, ZeitschritteData } from '../../../../../types/common';
import { csvFilesApi } from '@/api';
import { useTrainingQuota } from '../../../hooks/useTrainingQuota';
import { createEmptyEntry, sanitizeValue, processCSVContent, calculateTransferredData } from '../../../utils/csvProcessor';
import { instantUploadFile } from '../../../services/InstantUploadService';

export interface UseCSVFilesReturn {
  entries: CSVFileEntry[];
  setEntries: Dispatch<SetStateAction<CSVFileEntry[]>>;
  isLoading: boolean;
  errorMessages: string[];
  successMessage: string | null;
  duplicateBezeichnungIds: Set<string>;
  fetchCSVFiles: () => Promise<void>;
  handleInputChange: (id: string, field: keyof CSVFileEntry, value: string) => void;
  handleRadioChange: (id: string, value: 'ja' | 'nein', field: 'skalierung' | 'mittelwertbildungÜberDenZeithorizont') => void;
  handleFileChange: (id: string, event: ChangeEvent<HTMLInputElement>) => void;
  removeEntry: (id: string) => Promise<void>;
  uploadEntry: (id: string) => Promise<void>;
  saveEntry: (id: string) => Promise<void>;
  isEntryDirty: (id: string) => boolean;
  clearError: () => void;
  clearSuccess: () => void;
}

export const useCSVFiles = (
  sessionId: string | undefined,
  onFilesChange?: (files: CSVFileEntry[]) => void,
  fileType: 'input' | 'output' = 'input',
  zeitschritte?: ZeitschritteData,
  onUploadComplete?: () => void
): UseCSVFilesReturn => {
  const { t } = useTranslation('training');
  const { canProcessDataset, getQuotaError } = useTrainingQuota();
  
  const [entries, setEntries] = useState<CSVFileEntry[]>([createEmptyEntry(fileType)]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [duplicateBezeichnungIds, setDuplicateBezeichnungIds] = useState<Set<string>>(new Set());
  const [dirtyEntryIds, setDirtyEntryIds] = useState<Set<string>>(new Set());

  // Track entries with files for parent notification
  const [prevEntriesWithFiles, setPrevEntriesWithFiles] = useState<string>("");

  // FIX-8 (API storm): AbortController for in-flight fetch cleanup on unmount
  // or sessionId change. Prevents fetch-after-unmount warnings and state updates
  // on stale responses when user rapidly switches sessions.
  const fetchAbortRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef<boolean>(true);

  // FIX-8 (API storm): Stable ref for onFilesChange so the fetch effect doesn't
  // re-fire on parent re-renders that change the callback identity.
  const onFilesChangeRef = useRef(onFilesChange);
  useEffect(() => {
    onFilesChangeRef.current = onFilesChange;
  }, [onFilesChange]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (fetchAbortRef.current) {
        fetchAbortRef.current.abort();
      }
    };
  }, []);

  const clearError = useCallback(() => setErrorMessages([]), []);
  const clearSuccess = useCallback(() => setSuccessMessage(null), []);

  // Function to fetch CSV files from database
  const fetchCSVFiles = useCallback(async () => {
    if (!sessionId) return;

    // Abort any in-flight fetch before starting a new one
    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    setIsLoading(true);
    setErrorMessages([]);

    try {
      const filesData = await csvFilesApi.getCSVFiles(sessionId) as Record<string, unknown>[];

      // Bail out if request was superseded or component unmounted
      if (controller.signal.aborted || !isMountedRef.current) return;

      if (filesData && filesData.length > 0) {
        const csvFiles = filesData.filter((fileData) =>
          String(fileData.type).toLowerCase() === fileType
        );

        const mappedEntries: CSVFileEntry[] = csvFiles.map((fileData) => ({
          id: fileData.id as string,
          file: null,
          fileName: fileData.file_name as string,
          bezeichnung: fileData.bezeichnung as string,
          utcMin: sanitizeValue(fileData.utc_min),
          utcMax: sanitizeValue(fileData.utc_max),
          zeitschrittweite: sanitizeValue(fileData.zeitschrittweite),
          offset: sanitizeValue(fileData.offset),
          datenpunkte: sanitizeValue(fileData.datenpunkte),
          numerischeDatenpunkte: sanitizeValue(fileData.numerische_datenpunkte),
          numerischerAnteil: sanitizeValue(fileData.numerischer_anteil),
          min: sanitizeValue(fileData.min),
          max: sanitizeValue(fileData.max),
          datenform: sanitizeValue(fileData.datenform) as DatenformOption,
          zeithorizontStart: sanitizeValue(fileData.zeithorizont_start),
          zeithorizontEnd: sanitizeValue(fileData.zeithorizont_end),
          zeitschrittweiteTransferiertenDaten: sanitizeValue(fileData.zeitschrittweite_transferierten_daten),
          offsetTransferiertenDaten: sanitizeValue(fileData.offset_transferierten_daten),
          mittelwertbildungÜberDenZeithorizont: (sanitizeValue(fileData.mittelwertbildung_uber_den_zeithorizont) || 'nein') as 'ja' | 'nein',
          datenanpassung: sanitizeValue(fileData.datenanpassung) as DatenanpassungOption,
          zeitschrittweiteMinValue: sanitizeValue(fileData.zeitschrittweite_min),
          zeitschrittweiteAvgValue: sanitizeValue(fileData.zeitschrittweite_mittelwert),
          skalierung: (sanitizeValue(fileData.skalierung) || 'ja') as 'ja' | 'nein',
          skalierungMax: sanitizeValue(fileData.skalierung_max) || '1',
          skalierungMin: sanitizeValue(fileData.skalierung_min) || '0',
          storagePath: sanitizeValue(fileData.storage_path),
          type: (sanitizeValue(fileData.type) || 'input') as 'input' | 'output',
          createdAt: fileData.created_at ? new Date(String(fileData.created_at)).toISOString() : '',
          updatedAt: fileData.updated_at ? new Date(String(fileData.updated_at)).toISOString() : ''
        }));
        
        const finalEntries = mappedEntries.length > 0 ? [...mappedEntries, createEmptyEntry(fileType)] : [createEmptyEntry(fileType)];
        setEntries(finalEntries);
        onFilesChangeRef.current?.(mappedEntries);
      } else {
        setEntries([createEmptyEntry(fileType)]);
        onFilesChangeRef.current?.([]);
      }
    } catch (err) {
      // Swallow aborts (expected on unmount / session-change supersede)
      if (controller.signal.aborted || (err instanceof Error && err.name === 'AbortError')) {
        return;
      }
      if (!isMountedRef.current) return;
      setErrorMessages(['Error fetching files from database.']);
      setEntries([createEmptyEntry()]);
      onFilesChangeRef.current?.([]);
    } finally {
      if (isMountedRef.current && !controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [sessionId, fileType]);

  // Fetch CSV files from database when sessionId is available
  useEffect(() => {
    fetchCSVFiles();
  }, [fetchCSVFiles]);

  // Get N value based on fileType:
  // - 'input' uses zeitschritte.eingabe (MTS.I_N - Anzahl der Zeitschritte der Eingabedaten)
  // - 'output' uses zeitschritte.ausgabe (MTS.O_N - Anzahl der Zeitschritte der Ausgabedaten)
  const relevantN = fileType === 'output' ? zeitschritte?.ausgabe : zeitschritte?.eingabe;

  // Recalculate transferred data when the relevant N changes
  useEffect(() => {
    if (!relevantN) return;

    const N = parseFloat(relevantN);
    if (isNaN(N) || N <= 1) return;

    setEntries(prevEntries =>
      prevEntries.map(entry => {
        const th_strt = parseFloat(entry.zeithorizontStart);
        const th_end = parseFloat(entry.zeithorizontEnd);
        const csvOffset = parseFloat(entry.offset) || 0;

        // Only recalculate if we have valid values
        if (!isNaN(th_strt) && !isNaN(th_end) && th_end > th_strt) {
          const { delt_transf, ofst_transf } = calculateTransferredData(th_strt, th_end, csvOffset, N);
          return {
            ...entry,
            zeitschrittweiteTransferiertenDaten: delt_transf.toFixed(2),
            offsetTransferiertenDaten: typeof ofst_transf === 'number'
              ? ofst_transf.toFixed(2)
              : ofst_transf
          };
        }
        return entry;
      })
    );
  }, [relevantN]);

  // Notify parent about file changes when entries change
  useEffect(() => {
    if (onFilesChange) {
      const entriesWithFiles = entries.filter(entry => entry.file !== null || entry.storagePath);
      
      const currentEntriesString = JSON.stringify(entriesWithFiles.map(entry => ({
        id: entry.id,
        fileName: entry.fileName,
        bezeichnung: entry.bezeichnung,
        utcMin: entry.utcMin,
        utcMax: entry.utcMax,
        zeitschrittweite: entry.zeitschrittweite,
        min: entry.min,
        max: entry.max,
        offset: entry.offset,
        datenpunkte: entry.datenpunkte,
        numerischeDatenpunkte: entry.numerischeDatenpunkte,
        numerischerAnteil: entry.numerischerAnteil,
        datenform: entry.datenform,
        zeithorizontStart: entry.zeithorizontStart,
        zeithorizontEnd: entry.zeithorizontEnd,
        zeitschrittweiteTransferiertenDaten: entry.zeitschrittweiteTransferiertenDaten,
        offsetTransferiertenDaten: entry.offsetTransferiertenDaten,
        mittelwertbildungÜberDenZeithorizont: entry.mittelwertbildungÜberDenZeithorizont,
        datenanpassung: entry.datenanpassung,
        skalierung: entry.skalierung,
        skalierungMax: entry.skalierungMax,
        skalierungMin: entry.skalierungMin
      })));

      if (currentEntriesString !== prevEntriesWithFiles) {
        setPrevEntriesWithFiles(currentEntriesString);
        onFilesChange(entriesWithFiles);
      }
    }
  }, [entries, onFilesChange, prevEntriesWithFiles]);

  // Check for duplicate bezeichnung values
  useEffect(() => {
    const bezeichnungMap = new Map<string, string[]>();
    
    // Only check entries that have files (not empty placeholder entries)
    entries.forEach(entry => {
      if ((entry.file || entry.storagePath) && entry.bezeichnung.trim()) {
        const normalizedBezeichnung = entry.bezeichnung.trim().toLowerCase();
        const existingIds = bezeichnungMap.get(normalizedBezeichnung) || [];
        existingIds.push(entry.id);
        bezeichnungMap.set(normalizedBezeichnung, existingIds);
      }
    });
    
    // Find all IDs that have duplicates
    const duplicateIds = new Set<string>();
    bezeichnungMap.forEach((ids) => {
      if (ids.length > 1) {
        ids.forEach(id => duplicateIds.add(id));
      }
    });
    
    setDuplicateBezeichnungIds(duplicateIds);
  }, [entries]);

  // Function to check if bezeichnung is unique (not duplicate)
  const isBezeichnungUnique = useCallback((bezeichnung: string, entryId: string): boolean => {
    if (!bezeichnung.trim()) return false;
    const normalizedBezeichnung = bezeichnung.trim().toLowerCase();
    return !entries.some(entry =>
      entry.id !== entryId &&
      (entry.file || entry.storagePath) &&
      entry.bezeichnung?.trim().toLowerCase() === normalizedBezeichnung
    );
  }, [entries]);

  // Function to trigger upload when bezeichnung is entered and unique
  const triggerUploadForEntry = useCallback(async (entry: CSVFileEntry, newBezeichnung: string) => {
    // Only upload if:
    // 1. We have a sessionId
    // 2. Entry has a file but not yet uploaded (no storagePath)
    // 3. Bezeichnung is not empty and unique
    if (!sessionId || !entry.file || entry.storagePath || !newBezeichnung.trim()) {
      return;
    }

    // Check if bezeichnung is unique
    if (!isBezeichnungUnique(newBezeichnung, entry.id)) {
      return; // Don't upload - bezeichnung is duplicate
    }

    setIsLoading(true);
    try {
      const uploadResult = await instantUploadFile(entry.file, sessionId, {
        bezeichnung: newBezeichnung.trim(),
        type: fileType,
        // CSV metadata fields
        min: entry.min,
        max: entry.max,
        offset: entry.offset,
        datenpunkte: entry.datenpunkte,
        numerischeDatenpunkte: entry.numerischeDatenpunkte,
        numerischerAnteil: entry.numerischerAnteil,
        zeitschrittweite: entry.zeitschrittweite,
        utcMin: entry.utcMin,
        utcMax: entry.utcMax,
        // Drawer fields - these are entered by user before upload
        datenform: entry.datenform,
        datenanpassung: entry.datenanpassung,
        zeithorizontStart: entry.zeithorizontStart,
        zeithorizontEnd: entry.zeithorizontEnd,
        zeitschrittweiteTransferiertenDaten: entry.zeitschrittweiteTransferiertenDaten,
        offsetTransferiertenDaten: entry.offsetTransferiertenDaten,
        skalierung: entry.skalierung,
        skalierungMin: entry.skalierungMin,
        skalierungMax: entry.skalierungMax,
      });

      if (uploadResult.success) {
        // Update entry with database ID and storage path
        setEntries(prevEntries => {
          const updatedEntries = prevEntries.map(e =>
            e.id === entry.id
              ? {
                  ...e,
                  bezeichnung: newBezeichnung.trim(),
                  id: uploadResult.fileId, // Use UUID from database
                  storagePath: uploadResult.storagePath,
                  file: null // Clear File object since it's now in storage
                }
              : e
          );

          const allEntriesHaveData = updatedEntries.every(
            e => e.storagePath || e.file !== null
          );
          return allEntriesHaveData
            ? [...updatedEntries, createEmptyEntry(fileType)]
            : updatedEntries;
        });

        setSuccessMessage(t('csvFileUploader.messages.fileUploaded'));
        setTimeout(() => setSuccessMessage(null), 3000);
        
        // Notify parent that upload completed (for session info refresh)
        if (onUploadComplete) {
          onUploadComplete();
        }
      } else {
        setErrorMessages([uploadResult.error || 'Upload failed']);
      }
    } catch (error) {
      setErrorMessages([(error as Error)?.message || 'Upload error']);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, fileType, isBezeichnungUnique, t, onUploadComplete]);

  // Public function to upload entry on button click
  const uploadEntry = useCallback(async (id: string) => {
    const entry = entries.find(e => e.id === id);
    if (!entry) return;

    // Check if entry can be uploaded
    if (!entry.file || entry.storagePath) {
      return; // Already uploaded or no file
    }

    if (!entry.bezeichnung.trim()) {
      setErrorMessages([t('csvFileUploader.validation.bezeichnungRequired', 'Bezeichnung is required')]);
      return;
    }

    await triggerUploadForEntry(entry, entry.bezeichnung);
  }, [entries, triggerUploadForEntry, t]);

  // Save entry changes to database (for already uploaded entries)
  const saveEntry = useCallback(async (id: string) => {
    const entry = entries.find(e => e.id === id);
    if (!entry || !entry.storagePath) return;

    const updateData = {
      bezeichnung: entry.bezeichnung,
      datenform: entry.datenform,
      zeithorizontStart: entry.zeithorizontStart,
      zeithorizontEnd: entry.zeithorizontEnd,
      mittelwertbildungÜberDenZeithorizont: entry.mittelwertbildungÜberDenZeithorizont === 'ja',
      datenanpassung: entry.datenanpassung,
      skalierung: entry.skalierung,
      skalierungMin: entry.skalierungMin,
      skalierungMax: entry.skalierungMax,
      zeitschrittweiteTransferiertenDaten: entry.zeitschrittweiteTransferiertenDaten,
      offsetTransferiertenDaten: entry.offsetTransferiertenDaten,
    };

    setIsLoading(true);
    try {
      await csvFilesApi.updateCSVFile(id, updateData);

      // Remove from dirty set
      setDirtyEntryIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });

      setSuccessMessage(t('csvFileUploader.messages.changesSaved'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch {
      setErrorMessages([t('csvFileUploader.messages.saveError')]);
    } finally {
      setIsLoading(false);
    }
  }, [entries, t]);

  // Check if entry has unsaved changes
  const isEntryDirty = useCallback((id: string) => dirtyEntryIds.has(id), [dirtyEntryIds]);

  const handleInputChange = useCallback((id: string, field: keyof CSVFileEntry, value: string) => {
    setEntries(prevEntries => {
      return prevEntries.map(entry => {
        if (entry.id !== id) return entry;

        const updatedEntry = { ...entry, [field]: value };

        // Auto-calculate transferred data when zeithorizontStart or zeithorizontEnd changes
        if (field === 'zeithorizontStart' || field === 'zeithorizontEnd') {
          const th_strt = parseFloat(field === 'zeithorizontStart' ? value : entry.zeithorizontStart);
          const th_end = parseFloat(field === 'zeithorizontEnd' ? value : entry.zeithorizontEnd);
          const csvOffset = parseFloat(entry.offset) || 0;

          // Get N based on fileType:
          // - 'input' uses zeitschritte.eingabe (MTS.I_N)
          // - 'output' uses zeitschritte.ausgabe (MTS.O_N)
          const N = relevantN ? parseFloat(relevantN) : 13;

          // Only calculate if both values are valid numbers and N > 1
          if (!isNaN(th_strt) && !isNaN(th_end) && th_end > th_strt && N > 1) {
            const { delt_transf, ofst_transf } = calculateTransferredData(th_strt, th_end, csvOffset, N);
            updatedEntry.zeitschrittweiteTransferiertenDaten = delt_transf.toFixed(2);
            updatedEntry.offsetTransferiertenDaten = typeof ofst_transf === 'number'
              ? ofst_transf.toFixed(2)
              : ofst_transf;
          } else {
            // Clear calculated fields if inputs are invalid
            updatedEntry.zeitschrittweiteTransferiertenDaten = '';
            updatedEntry.offsetTransferiertenDaten = '';
          }
        }

        // Mark as dirty if already uploaded
        if (entry.storagePath) {
          setDirtyEntryIds(prev => new Set(prev).add(id));
        }

        return updatedEntry;
      });
    });
  }, [relevantN]);

  const handleRadioChange = useCallback((
    id: string,
    value: 'ja' | 'nein',
    field: 'skalierung' | 'mittelwertbildungÜberDenZeithorizont' = 'skalierung'
  ) => {
    setEntries(prevEntries =>
      prevEntries.map(entry => {
        if (entry.id !== id) return entry;

        // Mark as dirty if already uploaded
        if (entry.storagePath) {
          setDirtyEntryIds(prev => new Set(prev).add(id));
        }

        if (field === 'skalierung') {
          return {
            ...entry,
            skalierung: value,
            skalierungMin: value === 'nein' ? '' : entry.skalierungMin,
            skalierungMax: value === 'nein' ? '' : entry.skalierungMax
          };
        } else if (field === 'mittelwertbildungÜberDenZeithorizont') {
          return { ...entry, mittelwertbildungÜberDenZeithorizont: value };
        }
        return entry;
      })
    );
  }, []);

  const removeEntry = useCallback(async (id: string) => {
    const entryToRemove = entries.find(entry => entry.id === id);

    if (entryToRemove && entryToRemove.storagePath) {
      try {
        setIsLoading(true);
        setErrorMessages([]);

        await csvFilesApi.deleteCSVFile(id);

        setSuccessMessage(t('csvFileUploader.messages.fileDeleted'));
        setTimeout(() => setSuccessMessage(null), 3000);

      } catch (error: unknown) {
        setErrorMessages([t('csvFileUploader.errors.deleteFailed', { error: (error as Error)?.message || 'Unknown error' })]);
        setIsLoading(false);
        return;
      } finally {
        setIsLoading(false);
      }
    }

    setEntries(prevEntries => {
      const remainingEntries = prevEntries.filter(entry => entry.id !== id);

      // Ensure there's always at least one empty entry for new uploads
      const hasEmptyEntry = remainingEntries.some(entry => !entry.file && !entry.storagePath);

      if (!hasEmptyEntry) {
        return [...remainingEntries, createEmptyEntry(fileType)];
      }

      return remainingEntries;
    });
  }, [entries, t, fileType]);

  const handleFileChange = useCallback((id: string, event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;

    if (!canProcessDataset) {
      setErrorMessages([getQuotaError('processing')]);
      event.target.value = '';
      return;
    }

    const file = event.target.files[0];
    const reader = new FileReader();

    reader.onload = async (e) => {
      const content = e.target?.result as string;

      const lines = content.split('\n').filter(line => line.trim() !== '');

      if (lines.length === 0) {
        setErrorMessages([t('csvFileUploader.validation.fileEmpty')]);
        event.target.value = '';
        return;
      }

      const headerLine = lines[0];
      const headers = headerLine.split(';').map(h => h.trim());

      if (headers.length !== 2) {
        setErrorMessages([t('csvFileUploader.validation.exactlyTwoColumns', { count: headers.length })]);
        event.target.value = '';
        return;
      }

      if (headers[0].toUpperCase() !== 'UTC') {
        setErrorMessages([t('csvFileUploader.validation.firstColumnMustBeUtc', { found: headers[0] })]);
        event.target.value = '';
        return;
      }

      setErrorMessages([]);

      const metadata = processCSVContent(content);

      // Bezeichnung stays empty - user MUST enter it manually
      // Upload will happen when user enters a unique bezeichnung
      const updatedEntryData: Partial<CSVFileEntry> = {
        file,
        fileName: file.name,
        bezeichnung: '', // Empty - user must enter
        utcMin: metadata.utcMin,
        utcMax: metadata.utcMax,
        zeitschrittweite: metadata.zeitschrittweite,
        min: metadata.min,
        max: metadata.max,
        offset: metadata.offset,
        datenpunkte: metadata.datenpunkte,
        numerischeDatenpunkte: metadata.numerischeDatenpunkte,
        numerischerAnteil: metadata.numerischerAnteil,
        type: fileType
      };

      // Store file locally - upload will happen when user enters bezeichnung
      setEntries(prevEntries => {
        const updatedEntries = prevEntries.map(entry =>
          entry.id === id ? { ...entry, ...updatedEntryData } : entry
        );

        const allEntriesHaveFiles = updatedEntries.every(entry => entry.file !== null);
        return allEntriesHaveFiles ? [...updatedEntries, createEmptyEntry(fileType)] : updatedEntries;
      });
    };

    reader.readAsText(file);
  }, [canProcessDataset, getQuotaError, t, fileType, sessionId]);

  return {
    entries,
    setEntries,
    isLoading,
    errorMessages,
    successMessage,
    duplicateBezeichnungIds,
    fetchCSVFiles,
    handleInputChange,
    handleRadioChange,
    handleFileChange,
    removeEntry,
    uploadEntry,
    saveEntry,
    isEntryDirty,
    clearError,
    clearSuccess
  };
};
